import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, STORE_SLUG } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Minus, Plus, ShoppingCart, ChevronDown, Sparkles } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [avail, setAvail] = useState({});
  const [view, setView] = useState("front");

  const [colorIdx, setColorIdx] = useState(0);
  const [designKey, setDesignKey] = useState("");
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [backOption, setBackOption] = useState("front_only");
  const [playerName, setPlayerName] = useState("");
  const [playerNumber, setPlayerNumber] = useState("");
  const [team, setTeam] = useState("");

  useEffect(() => {
    api.get(`/stores/${STORE_SLUG}/products/${id}`).then((r) => setProduct(r.data)).catch(() => navigate("/"));
    api.get(`/stores/${STORE_SLUG}`).then((r) => setStore(r.data));
    api.get(`/stores/${STORE_SLUG}/inventory-availability`).then((r) => setAvail(r.data));
  }, [id, navigate]);

  // Preselect from home showcase / custom advert query params
  useEffect(() => {
    if (!product) return;
    if (params.get("custom") === "1" && product.allow_personalization) {
      setBackOption("name_number");
      setView("back");
    }
    const c = params.get("color");
    if (c) {
      const idx = (product.colors || []).findIndex((x) => (x.name || "").toLowerCase() === c.toLowerCase());
      if (idx >= 0) setColorIdx(idx);
    }
    const d = params.get("design");
    if (d) setDesignKey(d);
  }, [product, params]);

  const colors = useMemo(() => {
    if (!product) return [];
    return product.colors?.length ? product.colors : [{ name: "", hex: "#0A0A0A", fronts: {}, front_image: product.front_image, back_image: product.back_image }];
  }, [product]);
  const color = colors[colorIdx] || {};

  const frontDesigns = useMemo(() => {
    if (!product) return [];
    const all = product.front_designs?.length ? product.front_designs : [{ key: "default", label: "Front Design" }];
    const avail = all.filter((d) => color?.fronts?.[d.key] || d.key === "default");
    return avail.length ? avail : all;
  }, [product, color]);

  useEffect(() => {
    if (!frontDesigns.length) return;
    if (!designKey || !frontDesigns.some((d) => d.key === designKey)) setDesignKey(frontDesigns[0].key);
  }, [frontDesigns, designKey]);

  const backOptions = useMemo(() => {
    if (!product) return [];
    const opts = [{ key: "front_only", label: "Front Only" }, { key: "team_back", label: "Full-Team Roster Back" }];
    if (product.allow_personalization) opts.push({ key: "name_number", label: "Custom Player Name + Number" });
    return opts;
  }, [product]);

  const pricing = (product && product.pricing) || (store && store.pricing) || {};
  const unitPrice = useMemo(() => {
    if (!store || !product) return 0;
    const pr = product.pricing || store.pricing;
    const base = pr[backOption] ?? pr.front_only;
    const up = store.size_upcharges[size] || 0;
    return base + up;
  }, [store, product, backOption, size]);

  if (!product || !store) return <div className="max-w-7xl mx-auto px-6 py-20 text-gray-400">Loading…</div>;

  const frontImg = color.fronts?.[designKey] || color.front_image;
  const backImg = backOption === "name_number" ? (color.name_number_back || color.back_image) : color.back_image;
  const img = view === "front" ? frontImg : (backImg || frontImg);
  const designLabel = frontDesigns.find((d) => d.key === designKey)?.label || "";

  const handleAdd = () => {
    if (!size) return toast.error("Please select a size");
    addItem({
      product_id: product.id, product_name: product.name, size, quantity: qty,
      back_option: backOption, front_design: designKey, front_design_label: designLabel,
      player_name: playerName, player_number: playerNumber, team,
      garment_color: color.name || "", front_image: frontImg, back_image: color.back_image, unit_price: unitPrice,
    });
    toast.success("Added to cart");
    navigate("/checkout");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-28 lg:pb-12">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Images */}
        <div>
          <div className="aspect-square bg-black border border-white/10 overflow-hidden">
            <img src={img} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          <div className="flex gap-2 mt-3">
            {["front", "back"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`view-${v}-btn`}
                className={`flex-1 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${view === v ? "border-[var(--gv-blue)] text-[var(--gv-blue-bright)]" : "border-white/10 text-gray-400"}`}>
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Options */}
        <div>
          <h1 className="font-display text-4xl sm:text-5xl leading-none">{product.name}</h1>
          <p className="mt-3 text-3xl font-bold text-[var(--gv-blue-bright)]" data-testid="product-price">${unitPrice * qty}</p>
          <p className="mt-4 text-gray-300 text-sm leading-relaxed">{product.description}</p>
          <p className="mt-2 text-xs uppercase tracking-widest text-gray-500">{product.garment_note || "Team 365 TT11 Zone Performance T-Shirt • Moisture-wicking"}</p>

          {/* Garment color */}
          {colors.length > 0 && colors[0].name && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-2">Shirt Color: <span className="text-white">{color.name}</span></p>
              <div className="flex gap-2" data-testid="color-options">
                {colors.map((c, i) => (
                  <button key={c.name} onClick={() => setColorIdx(i)} data-testid={`color-${c.name.toLowerCase()}`} title={c.name}
                    className={`w-10 h-10 rounded-full border-2 transition-transform ${colorIdx === i ? "border-[var(--gv-blue-bright)] scale-110" : "border-white/20"}`}
                    style={{ backgroundColor: c.hex }} />
                ))}
              </div>
            </div>
          )}

          {/* Front design */}
          {frontDesigns.length > 1 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-2">Front Design: <span className="text-white">{designLabel}</span></p>
              <div className="grid grid-cols-2 gap-2" data-testid="front-design-options">
                {frontDesigns.map((d) => (
                  <button key={d.key} onClick={() => { setDesignKey(d.key); setView("front"); }} data-testid={`design-${d.key}`}
                    className={`p-1 border transition-colors ${designKey === d.key ? "border-[var(--gv-blue)] bg-[var(--gv-blue)]/10" : "border-white/10"}`}>
                    <div className="aspect-square bg-black overflow-hidden">
                      <img src={color.fronts?.[d.key]} alt={d.label} className="w-full h-full object-cover" />
                    </div>
                    <p className="text-[11px] font-bold mt-1 text-center leading-tight">{d.label}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Design / back option */}
          <div className="mt-6">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-2">Back Option</p>
            <div className="space-y-2">
              {backOptions.map((o) => (
                <button key={o.key} onClick={() => { setBackOption(o.key); if (o.key !== "front_only") setView("back"); }} data-testid={`backopt-${o.key}`}
                  className={`w-full flex items-center justify-between px-4 py-3 border text-left transition-colors ${backOption === o.key ? "border-[var(--gv-blue)] bg-[var(--gv-blue)]/10" : "border-white/10"}`}>
                  <span className="text-sm font-bold">{o.label}</span>
                  <span className="text-sm text-[var(--gv-blue-bright)] font-bold">${pricing[o.key]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Personalization */}
          {backOption === "name_number" && (
            <div className="mt-6 space-y-3" data-testid="personalization-fields">
              <div className="flex items-start gap-2 text-xs text-[var(--gv-blue-bright)]">
                <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>Add your player name & number now — or leave blank and add it at checkout. Custom tees are made to order — please allow <span className="font-bold">5–7 business days</span>.</p>
              </div>
              <input value={playerName} onChange={(e) => setPlayerName(e.target.value)} placeholder="Player Name" data-testid="player-name-input"
                className="w-full bg-[#141518] border border-white/10 focus:border-[var(--gv-blue)] outline-none px-4 py-3 text-sm" />
              <input value={playerNumber} onChange={(e) => setPlayerNumber(e.target.value)} placeholder="Player Number" data-testid="player-number-input"
                className="w-full bg-[#141518] border border-white/10 focus:border-[var(--gv-blue)] outline-none px-4 py-3 text-sm" />
              <div className="relative">
                <select value={team} onChange={(e) => setTeam(e.target.value)} data-testid="team-select"
                  className="w-full appearance-none bg-[#141518] border border-white/10 focus:border-[var(--gv-blue)] outline-none px-4 py-3 text-sm">
                  <option value="">Select Team</option>
                  {store.teams.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" />
              </div>
            </div>
          )}

          {/* Size — all sizes available */}
          <div className="mt-6">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-2">Size</p>
            <div className="grid grid-cols-4 gap-2">
              {store.sizes.map((s) => {
                const up = store.size_upcharges[s] || 0;
                return (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                    className={`py-3 border text-sm font-bold transition-colors relative ${size === s ? "border-[var(--gv-blue)] bg-[var(--gv-blue)]/10 text-white" : "border-white/10 text-gray-300 hover:border-white/30"}`}>
                    {s}{up > 0 && <span className="block text-[10px] text-[var(--gv-blue-bright)]">+${up}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Qty */}
          <div className="mt-6 flex items-center gap-4">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400">Qty</p>
            <div className="flex items-center border border-white/10">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="p-3"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" className="p-3"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          <button onClick={handleAdd} data-testid="add-to-cart-btn"
            className="hidden lg:flex mt-8 w-full items-center justify-center gap-2 bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-sm py-4 transition-colors">
            <ShoppingCart className="w-4 h-4" /> Add to Cart — ${unitPrice * qty}
          </button>
        </div>
      </div>

      {/* Sticky mobile add-to-cart */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A]/95 backdrop-blur border-t border-white/10 p-3">
        <button onClick={handleAdd} data-testid="add-to-cart-btn-mobile"
          className="w-full flex items-center justify-center gap-2 bg-[var(--gv-blue)] text-black font-bold uppercase tracking-widest text-sm py-4">
          <ShoppingCart className="w-4 h-4" /> Add to Cart — ${unitPrice * qty}
        </button>
      </div>
    </div>
  );
}
